import {startTransition, useEffect, useState} from 'react'

/**
 * Returns a list that is initially truncated to `initialCount` items, and then
 * returns the full list after the initial paint. This is useful in cases where
 * we want to display a large list of items, we don't want to virtualize (for
 * UX or accessibility reasons), but we also don't want the DOM reconciliation
 * for all of these elements to block the user from seeing the initial content.
 *
 * Uses `startTransition` so React can interrupt the full-list render to handle
 * user interactions, improving INP.
 */
export function useInitiallyTruncatedList<T>(allItems: T[], initialCount: number): {truncated: boolean; items: T[]} {
  const initiallyTruncated = allItems.length > initialCount
  const [truncated, setTruncated] = useState(initiallyTruncated)

  useEffect(() => {
    if (truncated) {
      startTransition(() => {
        setTruncated(false)
      })
    }
  }, [truncated])

  const items = truncated ? allItems.slice(0, initialCount) : allItems
  return {truncated, items}
}
